import { useState } from "react";
import { useBranding } from "./branding";
import portalApi, { formatErr } from "./portalApi";
import { Mail, CheckCircle2, ArrowRight } from "lucide-react";

export default function PortalLogin() {
  const { branding } = useBranding();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [debugLink, setDebugLink] = useState(null);
  const [err, setErr] = useState("");

  const primary = branding?.primary_color || "#0F4C81";

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true); setDebugLink(null);
    try {
      const { data } = await portalApi.post("/portal/request-link", { email: email.trim() });
      setSent(true);
      // In dev (stub email), surface the link so user can complete the flow
      if (data?.debug?.stubbed && data?.debug?.link) setDebugLink(data.debug.link);
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md mx-auto" data-testid="portal-login">
      <div className="bg-white border border-slate-200 rounded-lg p-7 sm:p-9 shadow-sm">
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
          Welcome back
        </h1>
        <p className="text-sm text-slate-500 mt-2">
          Enter your email and we'll send you a secure link to view your estimates and invoices. No password needed.
        </p>

        {!sent ? (
          <form onSubmit={submit} className="mt-7 space-y-4">
            {err && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded">
                {err}
              </div>
            )}
            <div>
              <label className="text-xs font-medium uppercase tracking-wider text-slate-700">
                Email address
              </label>
              <div className="relative mt-1">
                <Mail
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <input
                  data-testid="portal-email-input"
                  type="email"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full pl-9 pr-3 py-2.5 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-slate-900"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={busy}
              data-testid="portal-send-link-btn"
              style={{ background: primary }}
              className="w-full py-2.5 text-white text-sm rounded-md font-medium hover:opacity-90 disabled:opacity-60 flex items-center justify-center gap-2 transition-opacity"
            >
              {busy ? "Sending..." : (<>Email me a secure link <ArrowRight size={16} /></>)}
            </button>
          </form>
        ) : (
          <div className="mt-7 space-y-3" data-testid="portal-link-sent">
            <div className="flex items-start gap-3 bg-emerald-50 border border-emerald-200 rounded-md p-4">
              <CheckCircle2 className="text-emerald-600 mt-0.5 shrink-0" size={20} />
              <div className="text-sm text-emerald-900">
                <div className="font-semibold">Check your inbox</div>
                <div className="mt-1 text-emerald-800">
                  If <span className="font-medium">{email}</span> matches a customer account, a secure link is on its way. The link is valid for 30 minutes.
                </div>
              </div>
            </div>
            {debugLink && (
              <div className="text-xs text-slate-600 border border-amber-300 bg-amber-50 rounded p-3" data-testid="portal-debug-link">
                <div className="font-medium text-amber-900 mb-1">Dev mode — email not delivered</div>
                <a href={debugLink} className="text-[#0F4C81] underline break-all">Open magic link</a>
              </div>
            )}
            <button
              onClick={() => { setSent(false); setEmail(""); }}
              className="text-sm text-slate-500 hover:text-slate-900 underline"
            >
              Use a different email
            </button>
          </div>
        )}
      </div>

      {branding?.support_phone && (
        <p className="text-xs text-slate-500 text-center mt-6">
          Need help? Call us at <a href={`tel:${branding.support_phone}`} className="underline hover:text-slate-900">{branding.support_phone}</a>
        </p>
      )}
    </div>
  );
}
